import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import { getPricingPath } from "@/utils/localePaths";
import { getReadableErrorMessage } from "@/utils/errorHandler";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { User, Lock, ArrowLeft, Eye, EyeOff, Loader2, Edit, Save, X, Home, Settings, LogOut, Shield } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import Footer from "@/components/Footer";
import TrialBanner from "@/components/TrialBanner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// SEAT MODEL tek-kaynak koltuk limiti (TOPLAM, admin dahil) - backend max_users (:672/:686) ile AYNI.
//   /api/packages max_users donmedigi icin (yalniz katalog alanlari) yerel sabit; 3/5/2/4 gomulmez.
const PLAN_SEATS = { pro_monthly: 3, business_monthly: 5 };

function ProfilePage() {
  const { user, token, logout, refreshUserData, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { t, locale } = useTranslation();
  
  // Helper: Get locale-aware paths for navigation
  const getLocalePath = (route) => {
    const routes = {
      en: { home: '/en/', login: '/en/login', settings: '/en/settings', pricing: '/en/pricing' },
      tr: { home: '/tr/', login: '/tr/giris', settings: '/tr/hesap-ayarlari', pricing: '/tr/kredi-paketleri' },
      ar: { home: '/ar/', login: '/ar/login', settings: '/ar/settings', pricing: '/ar/pricing' },
      fr: { home: '/fr/', login: '/fr/login', settings: '/fr/settings', pricing: '/fr/pricing' },
      de: { home: '/de/', login: '/de/login', settings: '/de/settings', pricing: '/de/pricing' },
      es: { home: '/es/', login: '/es/login', settings: '/es/settings', pricing: '/es/pricing' }
    };
    return routes[locale]?.[route] || routes.en[route];
  };
  
  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  
  // Profile form state
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [profileType, setProfileType] = useState("");
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  
  // Password change state
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  
  // Team management state
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [newMemberEmail, setNewMemberEmail] = useState("");
  const [newMemberPassword, setNewMemberPassword] = useState("");
  const [newMemberFirstName, setNewMemberFirstName] = useState("");
  const [newMemberLastName, setNewMemberLastName] = useState("");
  const [addingMember, setAddingMember] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false); // Confirmation modal
  const [creditCost, setCreditCost] = useState(0); // SEAT MODEL: seats included, no per-seat fee
  
  // Invitation system states
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [sendingInvitation, setSendingInvitation] = useState(false);
  
  // Logout confirmation state
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  
  // 2FA states
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [show2FASetupModal, setShow2FASetupModal] = useState(false);
  const [show2FADisableModal, setShow2FADisableModal] = useState(false);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [twoFactorSecret, setTwoFactorSecret] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [loading2FA, setLoading2FA] = useState(false);
  const [disableCode, setDisableCode] = useState("");
  
  // OPARA v26.0: Expiring credits state
  const [expiringCredits, setExpiringCredits] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate(getLocalePath('login'));
    } else {
      // Initialize form with user data
      setEmail(user.email || "");
      setFirstName(user.first_name || "");
      setLastName(user.last_name || "");
      setProfileType(user.profile_type || "");
      setAdditionalInfo(user.additional_info || "");
      setMarketingOptIn(user.marketing_opt_in || false);
      
      // Fetch team members if user is team admin
      if (user.is_team_admin) {
        fetchTeamMembers();
      }
      
      // Fetch 2FA status
      fetch2FAStatus();
      
      // OPARA v26.0: Fetch expiring credits info
      fetchExpiringCredits();
    }
  }, [user, navigate, locale, authLoading]);

  // OPARA v26.0: Fetch expiring credits info
  const fetchExpiringCredits = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/user/expiring-credits`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.has_expiring) {
        setExpiringCredits(response.data);
      }
    } catch (error) {
      console.error('Kredi bilgisi alınamadı:', error);
    }
  };
  
  const fetch2FAStatus = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/2fa/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setTwoFactorEnabled(response.data.two_factor_enabled);
    } catch (error) {
      console.error('2FA durumu alınamadı:', error);
    }
  };
  
  const fetchTeamMembers = async () => {
    try {
      setLoadingTeam(true);
      
      // Fetch current team members
      const membersResponse = await axios.get(`${BACKEND_URL}/api/team/members`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setTeamMembers(membersResponse.data.members || []);
      
      // Fetch pending invitations
      try {
        const invitationsResponse = await axios.get(`${BACKEND_URL}/api/team/pending-invitations`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        setPendingInvitations(invitationsResponse.data.invitations || []);
      } catch (invError) {
        console.error('Bekleyen davetler yüklenemedi:', invError);
        setPendingInvitations([]);
      }
      
    } catch (error) {
      console.error('Takım üyeleri yüklenemedi:', error);
    } finally {
      setLoadingTeam(false);
    }
  };
  
  // SEAT MODEL TEK-KAYNAK sayac: admin(1) + uyeler + bekleyen davetler; backend cap ile ayni taban.
  //   (/team/members admin'i HARIC doner; /team/pending-invitations suresi-dolmusu HARIC doner.)
  const seatsUsed = 1 + teamMembers.length + pendingInvitations.length;
  const maxSeats = PLAN_SEATS[user?.plan_type] || 0;

  const handleOpenAddMember = () => {
    // SEAT MODEL tek-kaynak: buton zaten disabled -> bu guard IKINCI savunma katmani (kesin koruma).
    if (seatsUsed >= maxSeats) {
      if (user.plan_type === 'pro_monthly') {
        toast.error(t('profile.pro_limit_reached'));
      } else {
        toast.error(t('messages.team_limit_reached'));
      }
      return;
    }

    // SEAT MODEL: koltuklar plana dahil, koltuk basina ucret YOK
    setCreditCost(0);
    setShowConfirmModal(true);
  };
  
  const handleConfirmAddMember = () => {
    // Check if user has enough credits (skip for free members)
    if (creditCost > 0 && user.credits_remaining < creditCost) {
      toast.error(t('profile.insufficient_balance'));
      setShowConfirmModal(false);
      return;
    }
    
    setShowConfirmModal(false);
    setShowAddMemberModal(true);
  };
  
  const handleAddTeamMember = async (e) => {
    e.preventDefault();
    
    setSendingInvitation(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/team/invite-member`,
        {
          email: newMemberEmail,
          locale  // arayuz dili -> davet maili + kabul linki bu dilde
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      const message = creditCost === 0
        ? t('profile.invite_sent_free')
        : t('profile.invite_sent_paid', { credits: creditCost });
      toast.success(message);
      
      setShowAddMemberModal(false);
      setNewMemberEmail("");
      
      // Refresh team data and user credits
      await fetchTeamMembers();
      await refreshUserData();
    } catch (error) {
      toast.error(getReadableErrorMessage(error, t, 'profile.invite_send_failed'));
    } finally {
      setSendingInvitation(false);
    }
  };
  
  const handleRemoveTeamMember = async (memberId) => {
    if (!confirm(t('profile.confirm_remove_member'))) {
      return;
    }
    
    try {
      await axios.delete(`${BACKEND_URL}/api/team/remove-member/${memberId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      toast.success(t('messages.team_member_removed'));
      await fetchTeamMembers();
    } catch (error) {
      toast.error(getReadableErrorMessage(error, t, 'profile.member_remove_failed'));
    }
  };
  
  const handleCancelInvitation = async (invitationId) => {
    if (!confirm(t('profile.confirm_cancel_invite'))) {
      return;
    }
    
    try {
      await axios.delete(`${BACKEND_URL}/api/team/cancel-invitation/${invitationId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      toast.success(t('profile.invite_cancelled'));
      await fetchTeamMembers();
      await refreshUserData(); // Refresh to update credits if refunded
    } catch (error) {
      toast.error(getReadableErrorMessage(error, t, 'profile.invite_cancel_failed'));
    }
  };
  
  // 2FA Functions
  const handleSetup2FA = async () => {
    setLoading2FA(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/2fa/setup`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setQrCodeData(response.data.qr_code);
      setTwoFactorSecret(response.data.secret);
      setShow2FASetupModal(true);
    } catch (error) {
      toast.error(getReadableErrorMessage(error, t, 'profile.twofa_setup_failed'));
    } finally {
      setLoading2FA(false);
    }
  };
  
  const handleVerify2FA = async () => {
    if (verificationCode.length !== 6) {
      toast.error(t('auth.login.error_2fa_code_required'));
      return;
    }
    
    setLoading2FA(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/2fa/verify`, {
        totp_code: verificationCode
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success(t('profile.twofa_enabled'));
      // State güncellemesi yerine hard reload - beyaz ekran fix
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error("2FA verify error:", error);
      toast.error(getReadableErrorMessage(error, t, 'auth.login.error_2fa_wrong'));
      setLoading2FA(false);
    }
  };
  
  const handleDisable2FA = async () => {
    if (disableCode.length !== 6) {
      toast.error(t('auth.login.error_2fa_code_required'));
      return;
    }
    
    setLoading2FA(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/2fa/disable`, {
        totp_code: disableCode
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success(t('profile.twofa_disabled'));
      // State güncellemesi yerine hard reload
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error("2FA disable error:", error);
      toast.error(getReadableErrorMessage(error, t, 'auth.login.error_2fa_wrong'));
      setLoading2FA(false);
    }
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleConfirmLogout = () => {
    setShowLogoutModal(false);
    logout();
    navigate(getLocalePath('login'));
  };

  // Plan display name mapping
  const getPlanDisplayName = (planType) => {
    const planNames = {
      "free": t('plan.free'),
      "package_10": t('profile.plan_package_10'),
      "package_50": t('profile.plan_package_50'),
      "package_100": t('profile.plan_package_100'),
      "pro_monthly": t('profile.plan_pro'),
      "business_monthly": t('profile.plan_business'),
      "persona_staff": t('plan.staff') + t('profile.unlimited_suffix')
    };
    return planNames[planType] || planType;
  };

  // Remaining time calculation
  const getRemainingTime = () => {
    if (!user?.subscription_end_date) return null;
    
    const now = new Date();
    const endDate = new Date(user.subscription_end_date);
    const diff = endDate - now;
    
    if (diff <= 0) return t('profile.package_expired');
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (days > 0) {
      return t('profile.time_dhm', { days, hours, minutes });
    } else if (hours > 0) {
      return t('profile.time_hm', { hours, minutes });
    } else {
      return t('profile.time_m', { minutes });
    }
  };

  const handleCancelEdit = () => {
    // Reset form to original values
    setFirstName(user.first_name || "");
    setLastName(user.last_name || "");
    setProfileType(user.profile_type || "");
    setAdditionalInfo(user.additional_info || "");
    setMarketingOptIn(user.marketing_opt_in || false);
    setIsEditing(false);
    setShowPasswordSection(false);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    
    // Validate password if changing
    if (showPasswordSection && newPassword) {
      if (newPassword !== confirmPassword) {
        toast.error(t('profile.password_mismatch'));
        return;
      }
      if (newPassword.length < 6) {
        toast.error(t('profile.password_min_6'));
        return;
      }
      if (!currentPassword) {
        toast.error(t('profile.password_required'));
        return;
      }
    }

    setLoading(true);

    try {
      const updateData = {
        email: email,
        first_name: firstName,
        last_name: lastName,
        profile_type: profileType,
        additional_info: additionalInfo,
        marketing_opt_in: marketingOptIn
      };

      // Add password fields if changing password
      if (showPasswordSection && newPassword) {
        updateData.current_password = currentPassword;
        updateData.new_password = newPassword;
      }

      const response = await axios.put(
        `${BACKEND_URL}/api/user/profile`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      toast.success(t('profile.profile_updated'));
      
      // Refresh user data
      await refreshUserData();
      
      // Exit edit mode and reset password fields
      setIsEditing(false);
      setShowPasswordSection(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(getReadableErrorMessage(error, t, 'profile.profile_update_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCancelModal = () => {
    setShowCancelModal(true);
  };

  const handleConfirmCancelSubscription = async () => {
    setCancellingSubscription(true);
    setShowCancelModal(false);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/user/cancel-subscription`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      toast.success(t('profile.subscription_cancelled'));
      
      // Refresh user data to show cancelled status
      await refreshUserData();
    } catch (error) {
      toast.error(getReadableErrorMessage(error, t, 'profile.subscription_cancel_failed'));
    } finally {
      setCancellingSubscription(false);
    }
  };


  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black flex flex-col">
      {/* Header - Same as HomePage */}
      <header className="sticky top-0 z-50 bg-gradient-to-b from-black via-gray-900 to-black pt-2 pb-1 px-4">
        <div className="mb-1 px-4">
          <div className="flex justify-between items-center gap-4 bg-gray-900/50 backdrop-blur-sm rounded-lg p-2 border border-gray-800">
            {/* Sol: Logo - Tıklanabilir, Ana Sayfaya Dön */}
            <div 
              className="cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => navigate(getLocalePath('home'))}
            >
              <img 
                src="/opara-logo-trimmed.png" 
                alt="OPARA Logo" 
                className="h-10 w-auto object-contain"
              />
            </div>
            
            {/* Sağ: Analiz Hakkı ve Avatar */}
            <div className="flex items-center gap-2">
              {/* Analiz Hakkı */}
              <div className="text-sm bg-gray-800 px-3 py-1 rounded-lg border border-gray-700">
                <span className="text-white">{t('profile.credits_label')}</span>
                <span className="text-white font-bold">
                  {user?.plan_type === "persona_staff" ? "∞" : user?.credits_remaining}
                </span>
              </div>
            
              {/* Paket Yükle Butonu */}
              <Link to={getLocalePath('pricing')}>
                <Button className="bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold py-1 text-sm">
                  {t('nav.upgrade')}
                </Button>
              </Link>
            
              {/* Avatar Dropdown Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FFDD59] to-[#E6C84D] flex items-center justify-center hover:opacity-90 transition-opacity cursor-pointer border-2 border-gray-700 hover:border-[#FFDD59]">
                    <User className="w-4 h-4 text-black" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 bg-gray-900 border-gray-700" align="end">
                  <DropdownMenuLabel className="text-white">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium">
                        {user?.first_name} {user?.last_name}
                        {user?.plan_type === "persona_staff" && (
                          <span className="text-xs text-[#FFDD59] ml-2">{t('nav.staff_badge')}</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-400">
                        Plan: <span className="text-[#FFDD59]">{getPlanDisplayName(user?.plan_type)}</span>
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-gray-700" />
                  
                  <DropdownMenuItem asChild className="cursor-pointer text-gray-300 hover:text-white hover:bg-gray-800">
                    <Link to={getLocalePath('settings')} className="flex items-center">
                      <Settings className="w-4 h-4 mr-2" />
                      {t('nav.account_settings')}
                    </Link>
                  </DropdownMenuItem>
                  
                  <DropdownMenuSeparator className="bg-gray-700" />
                  
                  <DropdownMenuItem 
                    onClick={handleLogoutClick}
                    className="cursor-pointer text-red-400 hover:text-red-300 hover:bg-gray-800"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Back Button - Same as AboutPage */}
      <div className="px-4 mb-2">
        <div className="px-4">
          <Button 
            onClick={() => navigate(-1)}
            className="bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold"
          >
            {t('nav.back')}
          </Button>
        </div>
      </div>

      {/* Page Title */}
      <div className="text-center my-2">
        <h1 className="text-3xl font-bold text-[#FFDD59]">{t('profile.title')}</h1>
      </div>

      <div className="flex-grow">
        <div className="max-w-4xl mx-auto px-4 pb-8">

        {/* OPARA v7.4: Trial Banner - Show for free plan users */}
        {user && user.plan_type === 'free' && (
          <TrialBanner user={user} locale={locale} />
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Plan Info Card */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white text-lg">{t('profile.plan_info')}</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {/* 1. Current Plan */}
              <div>
                <p>
                  <span className="text-sm font-medium text-gray-400">{t('profile.current_plan')}:</span>{" "}
                  <span className="text-sm font-semibold text-white">
                    {getPlanDisplayName(user.plan_type)}
                  </span>
                </p>
              </div>

              {/* 2. Start Date - Only for Pro/Business */}
              {user && ["pro_monthly", "business_monthly"].includes(user.plan_type) && user.subscription_start_date && (
                <div>
                  <p>
                    <span className="text-sm font-medium text-gray-400">{t('profile.start_date')}:</span>{" "}
                    <span className="text-sm font-semibold text-white">
                      {new Date(user.subscription_start_date).toLocaleDateString(locale === 'tr' ? 'tr-TR' : locale === 'de' ? 'de-DE' : locale === 'fr' ? 'fr-FR' : locale === 'es' ? 'es-ES' : locale === 'ar' ? 'ar-SA' : 'en-US', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </span>
                  </p>
                </div>
              )}

              {/* 3. Remaining Credits */}
              <div>
                <p>
                  <span className="text-sm font-medium text-gray-400">{t('profile.remaining_credits')}:</span>{" "}
                  <span className="text-sm font-semibold text-white">
                    {user.credits_remaining}
                  </span>
                </p>
                
                {/* OPARA v26.0: Expiring Credits Warning */}
                {expiringCredits && expiringCredits.has_expiring && (
                  <div className="mt-2 p-2 bg-yellow-900/30 border border-yellow-600/50 rounded-md">
                    <p className="text-xs text-yellow-400">
                      ⚠️ <span className="font-medium">{expiringCredits.amount} {t('profile.credits_expiring')}</span> {expiringCredits.days_left} {t('profile.days')} ({new Date(expiringCredits.expires_at).toLocaleDateString(locale === 'tr' ? 'tr-TR' : locale === 'de' ? 'de-DE' : locale === 'fr' ? 'fr-FR' : locale === 'es' ? 'es-ES' : locale === 'ar' ? 'ar-SA' : 'en-US')})
                      <br />
                      <span className="text-yellow-500/70">{t('profile.credits_no_rollover')}</span>
                    </p>
                  </div>
                )}
              </div>

              {/* 4. Subscription Status - Only for Pro/Business (iptal-flag'i yansitir) */}
              {user && ["pro_monthly", "business_monthly"].includes(user.plan_type) && (
                <div>
                  <p>
                    <span className="text-sm font-medium text-gray-400">{t('profile.subscription_status')}:</span>{" "}
                    {user.subscription_cancelled || user.cancelled_at ? (
                      <span className="text-sm font-semibold text-amber-400">
                        {t('profile.subscription_cancelling')}
                      </span>
                    ) : (
                      <span className="text-sm font-semibold text-green-400">
                        {t('profile.active')}
                      </span>
                    )}
                  </p>
                </div>
              )}

              {/* 5. Renewal/End Date - Only for Pro/Business */}
              {user && ["pro_monthly", "business_monthly"].includes(user.plan_type) && user.subscription_end_date && (
                <div className="mb-6">
                  <p>
                    <span className="text-sm font-medium text-gray-400">
                      {user.subscription_cancelled || user.cancelled_at ? t('profile.end_date') : t('profile.renewal_date')}:
                    </span>{" "}
                    <span className="text-sm font-semibold text-white">
                      {new Date(user.subscription_end_date).toLocaleDateString(locale === 'tr' ? 'tr-TR' : locale === 'de' ? 'de-DE' : locale === 'fr' ? 'fr-FR' : locale === 'es' ? 'es-ES' : locale === 'ar' ? 'ar-SA' : 'en-US', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </span>
                  </p>
                </div>
              )}

              {/* Team Management Section - Only for Pro/Business admins */}
              {user && user.is_team_admin && ["pro_monthly", "business_monthly"].includes(user.plan_type) && (
                <div className="border-t border-b border-gray-700 pt-4 pb-4 mt-4 mb-4">
                  {/* Team Limit Info */}
                  <div className="bg-gray-800 rounded-lg p-3 mb-3">
                    <p>
                      <span className="text-sm font-medium text-gray-400">👥 {t('profile.team')}:</span>{" "}
                      {user.plan_type === 'pro_monthly' && (
                        <>
                          <span className="text-sm font-semibold text-white">{seatsUsed} / {maxSeats}</span>
                          <span className="text-sm text-gray-300"> {t('profile.including_you')}</span>
                        </>
                      )}
                      {user.plan_type === 'business_monthly' && (
                        <>
                          <span className="text-sm font-semibold text-white">{seatsUsed} / {maxSeats}</span>
                          <span className="text-sm text-gray-300"> {t('profile.including_you')}</span>
                        </>
                      )}
                    </p>
                    {/* Note - Show only when limit is reached */}
                    {seatsUsed >= maxSeats && (
                      <p className="text-xs text-gray-400 mt-2">
                        {t('profile.extra_member_note')}
                      </p>
                    )}
                  </div>

                  {/* Pending Invitations List */}
                  {pendingInvitations.length > 0 && (
                    <div className="mb-3">
                      <p className="text-base text-gray-400 mb-2">📧 {t('profile.pending_invitations')}:</p>
                      <div className="space-y-2">
                        {pendingInvitations.map((invitation) => (
                          <div key={invitation.id} className="bg-gray-800/50 border border-gray-700 rounded-lg p-3 flex items-center justify-between">
                            <div>
                              <p className="text-white font-medium">{invitation.email}</p>
                              <p className="text-xs text-gray-500">
                                {new Date(invitation.created_at).toLocaleDateString(locale === 'tr' ? 'tr-TR' : locale === 'de' ? 'de-DE' : locale === 'fr' ? 'fr-FR' : locale === 'es' ? 'es-ES' : locale === 'ar' ? 'ar-SA' : 'en-US')}
                              </p>
                            </div>
                            <Button
                              onClick={() => handleCancelInvitation(invitation.id)}
                              variant="outline"
                              size="sm"
                              className="border-gray-600 text-gray-300 hover:bg-gray-700"
                            >
                              {t('profile.cancel_invitation')}
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Team Members List */}
                  {teamMembers.length > 0 && (
                    <div className="space-y-2 mb-3">
                      {teamMembers.map((member) => (
                        <div key={member.id} className="bg-gray-800 rounded-lg p-3 flex items-center justify-between">
                          <div>
                            <p className="text-white font-medium">{member.first_name} {member.last_name}</p>
                            <p className="text-base text-gray-400">{member.email}</p>
                          </div>
                          <Button
                            onClick={() => handleRemoveTeamMember(member.id)}
                            variant="destructive"
                            size="sm"
                            className="bg-red-600 hover:bg-red-700"
                          >
                            {t('profile.remove_member_btn')}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add Team Member Button */}
                  <Button
                    onClick={handleOpenAddMember}
                    disabled={seatsUsed >= maxSeats}
                    className="w-full bg-gray-700 hover:bg-gray-600 text-white font-bold text-base"
                  >
                    + {t('profile.add_team_member')}
                  </Button>
                </div>
              )}

              {/* Eylem butonu ALT-UYEYE GIZLI (plan bilgisi ustte gorunmeye devam eder).
                  Gate: !parent_user_id (alt-uye kesin belirteci). :716 is_team_admin YERINE bunu
                  kullaniyoruz: is_team_admin gate'i, is_team_admin=false olan UCRETSIZ/SOLO
                  kullanicinin da Upgrade butonunu gizlerdi (regresyon). !parent_user_id yalniz
                  alt-uyeyi eler; solo/free/admin davranisi BYTE-AYNI. */}
              {user && !user.parent_user_id && (
                <Link to={getPricingPath(locale)}>
                  <Button className="w-full bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold mt-4 text-base">
                    {user.plan_type === 'business_monthly' ? t('profile.add_package') : t('profile.upgrade_or_add')}
                  </Button>
                </Link>
              )}

              {/* 2FA Section */}
              <div className="mt-6 pt-4 border-t border-gray-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-gray-400" />
                    <span className="text-sm font-medium text-gray-400">{t('profile.two_factor_auth')}:</span>
                  </div>
                  <span className={`text-sm font-semibold ${twoFactorEnabled ? 'text-green-400' : 'text-gray-400'}`}>
                    {twoFactorEnabled ? `✓ ${t('profile.enabled')}` : `✗ ${t('profile.disabled')}`}
                  </span>
                </div>
                
                <div className="mt-3">
                  {twoFactorEnabled ? (
                    <Button
                      onClick={() => setShow2FADisableModal(true)}
                      variant="outline"
                      className="w-full border-red-500 text-red-500 hover:bg-red-500 hover:text-white text-sm"
                    >
                      <Shield className="w-4 h-4 mr-2" />
                      {t('profile.disable_2fa')}
                    </Button>
                  ) : (
                    <Button
                      onClick={handleSetup2FA}
                      disabled={loading2FA}
                      className="w-full bg-green-600 hover:bg-green-700 text-white text-sm"
                    >
                      {loading2FA ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Shield className="w-4 h-4 mr-2" />
                      )}
                      {t('profile.enable_2fa')}
                    </Button>
                  )}
                </div>
                
                <p className="text-xs text-gray-500 mt-2">
                  {t('profile.2fa_description')}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* OLD Team Management Card - REMOVED - Now integrated in Plan Bilgileri card */}

          {/* Profile Info Card */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white text-lg">{t('profile.profile_info')}</CardTitle>
                {!isEditing && (
                  <Button
                    size="sm"
                    onClick={() => setIsEditing(true)}
                    className="bg-gray-700 hover:bg-gray-600 text-white text-base"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    {t('profile.edit')}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {!isEditing ? (
                // View Mode
                <div className="space-y-3">
                  <div>
                    <p>
                      <span className="text-sm font-medium text-gray-400">{t('profile.email_label')}:</span>{" "}
                      <span className="text-sm font-semibold text-white">{user.email}</span>
                    </p>
                  </div>

                  <div>
                    <p>
                      <span className="text-sm font-medium text-gray-400">{t('profile.full_name')}:</span>{" "}
                      <span className="text-sm font-semibold text-white">
                        {user.first_name} {user.last_name}
                      </span>
                    </p>
                  </div>

                  <div>
                    <p>
                      <span className="text-sm font-medium text-gray-400">{t('profile.profile_type')}:</span>{" "}
                      <span className="text-sm font-semibold text-white">
                        {user.profile_type === "company" && t('profile.profile_type_company')}
                        {user.profile_type === "influencer" && t('profile.profile_type_influencer')}
                        {user.profile_type === "agency" && t('profile.profile_type_agency')}
                        {user.profile_type === "visitor" && t('profile.profile_type_visitor')}
                      </span>
                    </p>
                  </div>

                  <div>
                    <p>
                      <span className="text-sm font-medium text-gray-400">{t('profile.membership_date')}:</span>{" "}
                      <span className="text-sm font-semibold text-white">
                        {new Date(user.created_at).toLocaleDateString(locale === 'tr' ? 'tr-TR' : locale === 'de' ? 'de-DE' : locale === 'fr' ? 'fr-FR' : locale === 'es' ? 'es-ES' : locale === 'ar' ? 'ar-SA' : 'en-US', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        })}
                      </span>
                    </p>
                  </div>

                  <div>
                    <p>
                      <span className="text-sm font-medium text-gray-400">{t('profile.marketing_emails')}:</span>{" "}
                      <span className="text-sm font-semibold text-white">
                        {user.marketing_opt_in ? `✓ ${t('profile.allowed')}` : `✗ ${t('profile.not_allowed')}`}
                      </span>
                    </p>
                  </div>

                  <div className="mt-6 pt-4 border-t border-gray-700">
                    <p className="text-sm text-gray-400 text-center">
                      {t('profile.questions_support')}:{" "}
                      <a 
                        href="mailto:support@opara.co" 
                        className="text-[#FFDD59] hover:underline font-medium"
                      >
                        support@opara.co
                      </a>
                    </p>
                  </div>

                  <div className="mt-6 pt-4 border-t border-gray-700">
                    <Button
                      onClick={handleLogoutClick}
                      variant="outline"
                      className="w-full border-red-500 text-red-500 hover:bg-red-500 hover:text-white text-base"
                    >
                      {t('profile.logout')}
                    </Button>
                  </div>

                  {/* Cancel Subscription Button - ALT-UYEYE GIZLI (!parent_user_id).
                      Abonelik-sahibi (solo pro/business ya da team admin) her zaman parent_user_id'siz;
                      alt-uye parent_user_id dolu -> gormesin. (is_team_admin ile esdeger ama sub-user
                      belirteci olarak !parent_user_id daha dogrudan, Upgrade ile ayni desen.) */}
                  {user && !user.parent_user_id && ["pro_monthly", "business_monthly"].includes(user.plan_type) && !user.subscription_cancelled && !user.cancelled_at && (
                    <div className="mt-4">
                      <Button
                        onClick={handleOpenCancelModal}
                        disabled={cancellingSubscription}
                        variant="outline"
                        className="w-full border-red-500 text-red-500 hover:bg-red-500 hover:text-white text-base"
                      >
                        {cancellingSubscription ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            {t('profile.cancelling')}
                          </>
                        ) : (
                          t('profile.cancel_subscription')
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                // Edit Mode
                <form onSubmit={handleUpdateProfile} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t('profile.email_label')}
                    </label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t('profile.first_name')}
                    </label>
                    <Input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t('profile.last_name')}
                    </label>
                    <Input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t('profile.profile_type')}
                    </label>
                    <select
                      value={profileType}
                      onChange={(e) => setProfileType(e.target.value)}
                      required
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 text-white rounded-md"
                    >
                      <option value="company">{t('profile.profile_type_company')}</option>
                      <option value="influencer">{t('profile.profile_type_influencer')}</option>
                      <option value="agency">{t('profile.profile_type_agency')}</option>
                      <option value="visitor">{t('profile.profile_type_visitor')}</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t('profile.company_info')}
                    </label>
                    <Input
                      type="text"
                      value={additionalInfo}
                      onChange={(e) => setAdditionalInfo(e.target.value)}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>

                  <div className="flex items-start space-x-3 p-3 bg-gray-800/50 rounded-lg border border-gray-700">
                    <Checkbox
                      id="marketing"
                      checked={marketingOptIn}
                      onCheckedChange={setMarketingOptIn}
                      className="mt-0.5 border-2 border-white"
                    />
                    <label
                      htmlFor="marketing"
                      className="text-sm text-gray-300 cursor-pointer leading-relaxed"
                    >
                      {t('profile.marketing_opt_in')}
                    </label>
                  </div>

                  {/* Password Change Section */}
                  <div className="border-t border-gray-700 pt-4">
                    <Button
                      type="button"
                      onClick={() => setShowPasswordSection(!showPasswordSection)}
                      className="bg-gray-700 hover:bg-gray-600 text-white mb-2"
                    >
                      <Lock className="w-4 h-4 mr-2" />
                      {t('profile.change_password')}
                    </Button>

                    {showPasswordSection && (
                      <div className="space-y-3 mt-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            {t('profile.current_password')}
                          </label>
                          <div className="relative">
                            <Input
                              type={showCurrentPassword ? "text" : "password"}
                              value={currentPassword}
                              onChange={(e) => setCurrentPassword(e.target.value)}
                              className="bg-gray-800 border-gray-700 text-white pr-10"
                            />
                            <button
                              type="button"
                              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                            >
                              {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            {t('profile.new_password')}
                          </label>
                          <div className="relative">
                            <Input
                              type={showNewPassword ? "text" : "password"}
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              className="bg-gray-800 border-gray-700 text-white pr-10"
                            />
                            <button
                              type="button"
                              onClick={() => setShowNewPassword(!showNewPassword)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                            >
                              {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            {t('profile.confirm_password')}
                          </label>
                          <Input
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="bg-gray-800 border-gray-700 text-white"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-4">
                    <Button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ...
                        </>
                      ) : (
                        <>
                          <Save className="mr-2 h-4 w-4" />
                          {t('profile.save_changes')}
                        </>
                      )}
                    </Button>
                    <Button
                      type="button"
                      onClick={handleCancelEdit}
                      variant="outline"
                      className="flex-1 border-gray-700 text-gray-300 hover:bg-gray-800"
                    >
                      <X className="mr-2 h-4 w-4" />
                      {t('profile.cancel_edit')}
                    </Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Confirmation Modal - Step 1 */}
        {showConfirmModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <Card className="bg-gray-900 border-gray-800 w-full max-w-md">
              <CardHeader>
                <CardTitle className="text-white">{t('profile.add_user')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                  <p className="text-yellow-300 text-center font-semibold">
                    {creditCost === 0 ? (
                      <>
                        {t('profile.free_member_text').split('||').map((seg, i) =>
                          i % 2 === 1 ? <span key={i} className="text-green-400">{seg}</span> : seg
                        )}
                        <br />
                        <span className="text-sm text-gray-400">
                          {t('profile.business_first_3_free')}
                        </span>
                      </>
                    ) : (
                      <>
                        {t('profile.credit_deduct_text', { credits: creditCost }).split('||').map((seg, i) =>
                          i % 2 === 1 ? <span key={i} className="text-[#FFDD59]">{seg}</span> : seg
                        )}
                        <br />
                        {t('profile.confirm_question')}
                      </>
                    )}
                  </p>
                </div>
                
                <div className="text-sm text-gray-400 text-center">
                  {t('profile.current_balance')} <span className="text-white font-semibold">{user.credits_remaining}</span>
                </div>
                
                <div className="flex gap-3">
                  <Button
                    onClick={handleConfirmAddMember}
                    className="flex-1 bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold"
                  >
                    {t('profile.yes_continue')}
                  </Button>
                  <Button
                    onClick={() => setShowConfirmModal(false)}
                    variant="outline"
                    className="flex-1 border-gray-700 text-gray-300 hover:bg-gray-800"
                  >
                    {t('common.cancel')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Add Member Form Modal - Step 2 */}
        {showAddMemberModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <Card className="bg-gray-900 border-gray-800 w-full max-w-md">
              <CardHeader>
                <CardTitle className="text-white">{t('profile.invite_team_member')}</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAddTeamMember} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t('profile.email_address')}
                    </label>
                    <Input
                      type="email"
                      value={newMemberEmail}
                      onChange={(e) => setNewMemberEmail(e.target.value)}
                      required
                      className="bg-gray-800 border-gray-700 text-white"
                      placeholder="uye@example.com"
                    />
                  </div>
                  
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                    <p className="text-sm text-blue-300">
                      {t('profile.invite_link_info')}
                    </p>
                  </div>
                  
                  <div className="flex gap-3">
                    <Button
                      type="submit"
                      disabled={sendingInvitation}
                      className="flex-1 bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold"
                    >
                      {sendingInvitation ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {t('profile.sending_invite')}
                        </>
                      ) : (
                        t('profile.send_invite')
                      )}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => setShowAddMemberModal(false)}
                      variant="outline"
                      className="flex-1 border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white"
                    >
                      {t('profile.cancel_edit')}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Cancel Subscription Confirmation Modal */}
        {showCancelModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <Card className="bg-gray-900 border-gray-800 w-full max-w-md">
              <CardHeader>
                <CardTitle className="text-white text-center">{t('profile.cancel_confirm_title')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <p className="text-white text-center">
                    {t('profile.cancel_confirm_desc')}
                  </p>
                </div>
                
                <div className="flex gap-3">
                  <Button
                    onClick={handleConfirmCancelSubscription}
                    disabled={cancellingSubscription}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold"
                  >
                    {cancellingSubscription ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('profile.cancelling')}
                      </>
                    ) : (
                      t('profile.cancel_subscription')
                    )}
                  </Button>
                  <Button
                    onClick={() => setShowCancelModal(false)}
                    variant="outline"
                    className="flex-1 border-gray-700 text-gray-300 hover:bg-gray-800"
                    disabled={cancellingSubscription}
                  >
                    {t('profile.cancel_edit')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Logout Confirmation Modal */}
        {showLogoutModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <Card className="bg-gray-900 border-gray-800 w-full max-w-md">
              <CardHeader>
                <CardTitle className="text-white text-center">{t('profile.logout_confirm')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={handleConfirmLogout}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold"
                  >
                    {t('profile.yes_logout')}
                  </Button>
                  <Button
                    onClick={() => setShowLogoutModal(false)}
                    variant="outline"
                    className="flex-1 border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white"
                  >
                    {t('profile.cancel_edit')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* 2FA Setup Modal */}
        {show2FASetupModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <Card className="bg-gray-900 border-gray-800 w-full max-w-md">
              <CardHeader>
                <CardTitle className="text-white text-center">
                  <Shield className="w-6 h-6 inline-block mr-2 text-[#FFDD59]" />
                  {t('profile.2fa_setup_title')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-gray-800 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-300 mb-3">
                    {t('profile.2fa_description')}
                  </p>
                  {qrCodeData && (
                    <img 
                      src={`data:image/png;base64,${qrCodeData}`} 
                      alt="2FA QR Code" 
                      className="mx-auto w-48 h-48 bg-white p-2 rounded-lg"
                    />
                  )}
                </div>
                
                <div className="bg-gray-800/50 rounded-lg p-3">
                  <p className="text-xs text-gray-400 mb-1">Secret:</p>
                  <p className="text-sm text-[#FFDD59] font-mono break-all">{twoFactorSecret}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Code (6 digits)
                  </label>
                  <Input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="bg-gray-800 border-gray-700 text-white text-center text-2xl tracking-widest"
                    maxLength={6}
                  />
                </div>
                
                <div className="flex gap-3">
                  <Button
                    onClick={handleVerify2FA}
                    disabled={loading2FA || verificationCode.length !== 6}
                    className="flex-1 bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold"
                  >
                    {loading2FA ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Shield className="w-4 h-4 mr-2" />
                    )}
                    {t('profile.verify_and_activate')}
                  </Button>
                  <Button
                    onClick={() => {
                      setShow2FASetupModal(false);
                      setVerificationCode("");
                      setQrCodeData(null);
                    }}
                    variant="outline"
                    className="border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white"
                  >
                    {t('profile.cancel_edit')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* 2FA Disable Modal */}
        {show2FADisableModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <Card className="bg-gray-900 border-gray-800 w-full max-w-md">
              <CardHeader>
                <CardTitle className="text-white text-center">
                  {t('profile.disable_2fa')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <p className="text-white text-center text-sm">
                    ⚠️ {t('profile.2fa_description')}
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Code (6 digits):
                  </label>
                  <Input
                    type="text"
                    value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="bg-gray-800 border-gray-700 text-white text-center text-2xl tracking-widest"
                    maxLength={6}
                  />
                </div>
                
                <div className="flex gap-3">
                  <Button
                    onClick={handleDisable2FA}
                    disabled={loading2FA || disableCode.length !== 6}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold"
                  >
                    {loading2FA ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : null}
                    {t('profile.disable_2fa')}
                  </Button>
                  <Button
                    onClick={() => {
                      setShow2FADisableModal(false);
                      setDisableCode("");
                    }}
                    variant="outline"
                    className="flex-1 border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white"
                  >
                    {t('profile.cancel_edit')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        </div>
      </div>
      
      <Footer />
    </div>
  );
}

export default ProfilePage;
